<template>
  <div :class="wrapperClasses">
    <label v-if="label" :for="phoneInputId" class="ui-phone-input__label">
      {{ label }}
      <span v-if="required" class="ui-phone-input__required">*</span>
    </label>
    <div
      :class="['ui-phone-input__container', { 'ui-phone-input__container--focused': isFocused }]"
      ref="containerRef"
    >
      <vue-tel-input
        :id="phoneInputId"
        :model-value="modelValue"
        @update:model-value="handleUpdate"
        @country-changed="handleCountryChange"
        @blur="handleBlur"
        @focus="handleFocus"
        :defaultCountry="props.defaultCountry"
        :autoDefaultCountry="false"
        :onlyCountries="props.onlyCountries"
        :preferredCountries="props.preferredCountries || []"
        mode="international"
        :disabled="disabled"
        :placeholder="props.placeholder"
      />
    </div>
    <div v-if="error || helperText" class="ui-phone-input__helper">
      <span v-if="error" class="ui-phone-input__error">{{ error }}</span>
      <span v-else-if="helperText" class="ui-phone-input__helper-text">{{ helperText }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, watch, nextTick } from "vue";
import type { Size } from "./types";

interface Props {
  modelValue?: string;
  label?: string;
  size?: Size;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  id?: string;
  defaultCountry?: string;
  onlyCountries?: string[];
  preferredCountries?: string[];
  placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  disabled: false,
  required: false,
  defaultCountry: "US",
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  "update:country": [country: string];
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
}>();

const isFocused = ref(false);
const containerRef = ref<HTMLElement | null>(null);
const phoneInputId = computed(
  () => props.id || `phone-input-${Math.random().toString(36).substr(2, 9)}`
);

const wrapperClasses = computed(() => {
  const classes = ["ui-phone-input"];
  classes.push(`ui-phone-input--${props.size}`);
  if (props.disabled) classes.push("ui-phone-input--disabled");
  if (props.error) classes.push("ui-phone-input--error");
  return classes;
});

const setPlaceholder = () => {
  if (props.placeholder && containerRef.value) {
    nextTick(() => {
      const input = containerRef.value?.querySelector(".vue-tel-input input") as HTMLInputElement;
      if (input && props.placeholder) {
        input.placeholder = props.placeholder;
      }
    });
  }
};

onMounted(() => {
  setPlaceholder();
});

watch(
  () => props.placeholder,
  () => {
    setPlaceholder();
  }
);

watch(
  () => props.defaultCountry,
  () => {
    setPlaceholder();
  }
);

const handleUpdate = (value: string) => {
  emit("update:modelValue", value);
};

const handleCountryChange = (country: { iso2: string; name: string; dialCode?: string }) => {
  if (country && country.iso2) {
    emit("update:country", country.iso2.toUpperCase());
  }
};

const handleBlur = (event: FocusEvent) => {
  isFocused.value = false;
  emit("blur", event);
};

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;
  emit("focus", event);
};
</script>

<style scoped>
.ui-phone-input {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.ui-phone-input__label {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #344054;
  margin-bottom: 6px;
}

.ui-phone-input__required {
  color: #dc3545;
  font-size: 1.1rem;
  margin-left: 2px;
}

.ui-phone-input__container {
  position: relative;
  width: 100%;
}

.ui-phone-input__container :deep(.vue-tel-input) {
  width: 100%;
  height: 43px !important;
  display: flex;
  align-items: stretch;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
  background-color: #ffffff;
  transition: all 0.2s ease-in-out;
  font-family: "Inter", sans-serif;
}

.ui-phone-input__container :deep(.vue-tel-input:hover) {
  border-color: var(--color-border-hover);
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1);
}

.ui-phone-input__container--focused :deep(.vue-tel-input) {
  border-color: #3e3efe;
  box-shadow:
    0 0 0 3px rgba(62, 62, 254, 0.1),
    0px 1px 3px 0px rgba(0, 0, 0, 0.1);
}

.ui-phone-input__container :deep(.vue-tel-input .vti__dropdown) {
  border: none !important;
  border-radius: 7px 0 0 7px;
  background-color: transparent;
  height: 100%;
  padding: 0 10px;
  min-width: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  transition: background-color 0.15s ease;
}

.ui-phone-input__container :deep(.vue-tel-input .vti__dropdown:hover) {
  background-color: #f8f8f8;
  border: none !important;
}

.ui-phone-input__container :deep(.vue-tel-input .vti__dropdown-list) {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
}

.ui-phone-input__container :deep(.vue-tel-input input) {
  flex: 1;
  min-width: 0;
  font-family: "Inter", sans-serif;
  font-size: 0.82rem;
  font-weight: 400;
  color: #282a30;
  background-color: transparent;
  border: none;
  border-left: 1px solid #d0d5dd;
  border-radius: 0 7px 7px 0;
  padding: 10px 12px;
  height: 100%;
  margin: 0;
  box-sizing: border-box;
  outline: none;
}

.ui-phone-input__container :deep(.vue-tel-input input::placeholder) {
  color: #9ca3af;
  font-weight: 400;
  font-size: 0.82rem;
}

.ui-phone-input__container :deep(.vue-tel-input input:focus) {
  outline: none;
}

.ui-phone-input__helper {
  margin-top: 4px;
  font-size: 0.75rem;
}

.ui-phone-input__error {
  color: #ef4444;
}

.ui-phone-input__helper-text {
  color: #6b7280;
}

.ui-phone-input--sm .ui-phone-input__label {
  font-size: 11px;
}

.ui-phone-input--sm .ui-phone-input__container :deep(.vue-tel-input) {
  height: 36px !important;
}

.ui-phone-input--sm .ui-phone-input__container :deep(.vue-tel-input .vti__dropdown) {
  padding: 0 6px;
}

.ui-phone-input--sm .ui-phone-input__container :deep(.vue-tel-input input) {
  font-size: 0.75rem;
  padding: 8px 10px;
}

.ui-phone-input--lg .ui-phone-input__label {
  font-size: 13px;
}

.ui-phone-input--lg .ui-phone-input__container :deep(.vue-tel-input) {
  height: 48px !important;
}

.ui-phone-input--lg .ui-phone-input__container :deep(.vue-tel-input .vti__dropdown) {
  padding: 0 10px;
}

.ui-phone-input--lg .ui-phone-input__container :deep(.vue-tel-input input) {
  font-size: 0.9rem;
  padding: 12px 16px;
}

.ui-phone-input--error .ui-phone-input__container :deep(.vue-tel-input) {
  border-color: #ef4444;
}

.ui-phone-input--error .ui-phone-input__container--focused :deep(.vue-tel-input) {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.ui-phone-input--disabled .ui-phone-input__container :deep(.vue-tel-input) {
  background-color: #f9f9fc;
  opacity: 0.6;
  border-color: #e5e7eb;
  cursor: not-allowed;
}

.ui-phone-input--disabled .ui-phone-input__container :deep(.vue-tel-input .vti__dropdown),
.ui-phone-input--disabled .ui-phone-input__container :deep(.vue-tel-input input) {
  cursor: not-allowed;
}
</style>
